import { complete } from "@/api/ai";
import Calout from "@/ui/Calout";
import Panel from "@/ui/Panel";
import TextArea from "@/ui/TextArea";
import TextInput from "@/ui/TextInput";
import { TextInputFlavors } from "@/ui/TextInput/types";
import { AI_DOMAIN } from "@launchos/shared-utils/env";
import { MagicWand } from "@phosphor-icons/react";
import { Sparkle } from "lucide-react";
import { useState, useEffect } from "react";

interface ObjectTaggingModalProps {
  content?: any;
  html?: any;
  settings?: any;
  updateEditorComponent: (id: string, settings: any) => void;
  hideModal: () => void;
}

export const ObjectTaggingModal = (props: ObjectTaggingModalProps) => {
  const { content, html, settings, updateEditorComponent, hideModal } = props;

  // Local state for form fields - initialize with empty values
  const [formState, setFormState] = useState({
    label: "",
    prompt: "",
    description: "",
    // keywords: "",
    role: ""
  });

  // Loading state for AI generation
  const [isGenerating, setIsGenerating] = useState(false);

  // Update local state when settings change
  useEffect(() => {
    if (settings) {
      setFormState({
        label: settings.label || settings.name || settings.type || "",
        prompt: settings.prompt || "",
        description: settings.description || "",
        // keywords: settings.keywords || "",
        role: settings.role || ""
      });
    }
  }, [settings]);

  const onClose = () => {
    hideModal();
  };

  const onSuccess = () => {
    // Update component settings with current form state
    updateEditorComponent(settings.id, {
      ...settings,
      label: formState.label,
      prompt: formState.prompt,
      description: formState.description,
      // keywords: formState.keywords,
      role: formState.role
    });
    hideModal();
  };

  const handleInputChange = (field: string, value: string) => {
    setFormState(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const generateDescription = async () => {
    if (!formState.label.trim()) {
      alert("Please enter a label first");
      return;
    }

    setIsGenerating(true);
    try {      
      const response = await complete(
        `Generate a detailed analysis and description for the following group of web page elements:

        TYPE: ${settings?.type || "Group"}
        NAME: ${formState.label}
        ROLE: ${formState.role}

        You will receive the web page elements in two formats representing the same content:

        **HTML Format**: The rendered HTML structure
        \`\`\`html
        ${html}
        \`\`\`

        **JSON Format**: The structured page data with hierarchical relationships
        \`\`\`json
        ${JSON.stringify(content, null, 2)}
        \`\`\`

        Each element has a unique identifier that connects the HTML and JSON representations.

        Please provide the following in your response:
        1. A concise description of the group and what you see.
        2. A description of how the group is laid out (e.g., what appears first, second, on the left, right, etc.).
        3. An explanation of the circumstances or scenarios in which this group should be used on a website.
        4. The types of pages this group is most suited for (e.g., lead capture page, sales page, thank you page, upsell/downsell page, home page, etc.), and how it might be used in those contexts.
        5. Circumstances or scenarios where this group should NOT be used.
        6. A list of keywords or phrases to look out for when someone writes a prompt that indicates this group should be provided.
        7. A few sample queries or requests that people might use when asking for a group like this.

        The description (item 1) should be no more than 100 words and begin with the words: "This ${settings?.label || settings?.name || settings?.type || "Group"}"

        Format your response clearly, using bullet points or sections for each item above.
        `,
        "You are a helpful assistant that generates detailed, structured descriptions and usage guidelines for UI elements."
      );
      console.log("AI Response:", response);
      
      setFormState(prev => ({
        ...prev,
        description: response || ""
      }));
      
    } catch (error) {
      console.error("Error generating description:", error);
      alert("Failed to generate description. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* {JSON.stringify(settings)} */}
      <Calout title="Element Settings" caption="This is the element settings" />
      <TextInput
        flavor={TextInputFlavors.MODERN}
        label={`${settings?.type || "Element"} Label`}
        value={formState.label}
        placeholder={`${settings?.type || "Element"} Name`}
        name="Name"
        onChange={(e) => handleInputChange("label", e.target.value)}
      />
      <TextInput
        flavor={TextInputFlavors.MODERN}
        label={`${settings?.type || "Element"} Role`}
        value={formState.role}
        placeholder={`${settings?.type || "Element"} Role`}
        name="Role"
        onChange={(e) => handleInputChange("role", e.target.value)}
      />
      <TextArea
        flavor={TextInputFlavors.MODERN}
        label={`Group Description`}
        value={formState.description}
        inputClass="form-input min-h-[175px]"
        placeholder={` `}
        name="description"
        onChange={(e) => handleInputChange("description", e.target.value)}
      />
      {/* <TextInput
        flavor={TextInputFlavors.MODERN}
        label={`Keywords`}
        value={formState.keywords}
        placeholder={`Enter a list of keywords separated by commas`}
        name="Keywords"
        onChange={(e) => handleInputChange("keywords", e.target.value)}
      /> */}
      <TextInput
        flavor={TextInputFlavors.MODERN}
        label={`Question to show the user`}
        value={formState.prompt}
        placeholder={`Question to show the user`}
        name="Prompt"
        onChange={(e) => handleInputChange("prompt", e.target.value)}
      />
      <div className="flex justify-between items-center mt-8 gap-4">
        <div>
          <button
            type="button"
            className="btn flex gap-2"
            onClick={generateDescription}
            disabled={isGenerating || !formState.label.trim()}
            title="Generate Description"
          >
            <MagicWand size={16} className="mr-2" />
            <span>{isGenerating ? "Generating..." : "Generate"}</span>
          </button>
        </div>
        <div className="flex gap-4">
          <button
            type="button"
            className="btn bg-black/10 text-black/70"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            onClick={onSuccess}
            title="Save"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};